import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import { Box, Drawer, IconButton, List, Tooltip } from "@mui/material";
import { CloseRounded, MenuRounded } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { WinDims } from "../../hooks/Method";
import { useAppContext } from "context";
import { NavMenu } from "../../components/body";
import CompanyProfile from "../../components/body/CompanyProfile";
import { buildMenu } from "../../components/body/menuConfig";
import { pushRecent } from "../../utils/navPrefs";
import { isPosPath } from "../../utils/posPath";
import GlobalSearch from "./GlobalSearch";
import NavShortcuts from "./NavShortcuts";
import ModernHeaderActions from "./ModernHeaderActions";

const AppBar = styled(MuiAppBar)(() => ({
  top: 0,
  border: "none",
  borderRadius: 0,
  boxShadow: "var(--shadow-sm)",
  backgroundColor: "var(--white-color)",
  color: "var(--black-color)",
}));

const LG = 1024;

/**
 * Two-tier NetSuite-style shell for the modern UI template.
 *  Tier 1 (light): logo · global search · tools · user
 *  Tier 2 (dark) : Recent/Favorites/Home · main menu
 * Below `lg` it collapses to Tier 1 + a hamburger that opens the classic
 * sidebar (NavMenu). POS hides Tier 2 for a full-screen register and gets that
 * same hamburger at any width, so the register never becomes a dead end.
 */
export default function TopNavShell(props) {
  const { menu } = props;
  const location = useLocation();
  const { access, settings } = useAppContext();
  const { t } = useTranslation(["nav", "common"]);
  const width = WinDims().width;
  const isDesktop = width > LG;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const path = location.pathname || "";
  const inPOS = isPosPath(path);
  const showNavTier = isDesktop && !inPOS;

  // The register keeps the whole screen, so Tier 2 (Home/Recent/Favourites + the
  // module menu) is unmounted on POS — which used to leave the cashier with no way
  // out at all. The only replacement was a Home button gated on `dashboardAccess`,
  // so a cashier granted Inventory but not the dashboard saw nothing, and it pointed
  // at /home, the one route that requires the very flag being tested.
  //
  // The fix reuses the hamburger + sidebar drawer this shell already renders below
  // `lg`: on POS the desktop gets it too. That costs no vertical space, and it opens
  // the FULL permission-filtered menu rather than a single link — which matters,
  // because getDefaultRoute() resolves to /pos itself for a POS-first cashier, so a
  // "Home" link would have been a no-op for exactly the people who were stranded.
  const showNavDrawer = !isDesktop || inPOS;

  // Flat menu leaves for resolving the current page → "recent".
  const flat = useMemo(() => {
    const out = [];
    buildMenu(access, settings, t).forEach((g) => {
      if (g.children?.length) g.children.forEach((c) => out.push({ title: c.title, path: c.path }));
      else if (g.path) out.push({ title: g.title, path: g.path });
    });
    return out;
  }, [access, settings, t]);

  // Record the current page into "recent" whenever the route changes.
  useEffect(() => {
    const match = flat.find((f) => path.includes(f.path));
    if (match) pushRecent(match);
  }, [path, flat]);

  // Close the mobile drawer when navigation actually happens (route change) —
  // NOT on every click, so tapping a parent item just expands its children.
  useEffect(() => {
    setDrawerOpen(false);
  }, [path]);

  const bodyMt = showNavTier
    ? "calc(var(--utilitybar-height) + var(--navbar-height))"
    : "var(--utilitybar-height)";

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <AppBar position="fixed">
        {/* ---- Tier 1: utility bar (light) ---- */}
        <div
          className="flex items-center gap-2 w-full px-[12px]"
          style={{ height: "var(--utilitybar-height)" }}
        >
          {showNavDrawer && (
            <Tooltip title={t("nav:menu", "Menu")}>
              <IconButton
                onClick={() => setDrawerOpen((o) => !o)}
                size="small"
                sx={{ color: "var(--gray-color)" }}
                aria-label={t("nav:menu", "Menu")}
              >
                {drawerOpen ? <CloseRounded /> : <MenuRounded />}
              </IconButton>
            </Tooltip>
          )}

          {/* Company details (logo + company/outlet) in place of a fixed logo.
              min-w-0 + content-based width: shows the full name when there's
              room and only ellipsises when the bar runs out of space. */}
          <div className="min-w-0 shrink">
            <CompanyProfile light />
          </div>

          {/* The dashboard-gated "Home" button that used to sit here is gone — the
              hamburger above replaces it, and unlike that button it works for every
              permission set. See showNavDrawer. */}

          {/* Global search — centre. Kept on POS too: it navigates anywhere in the
              menu and answers Ctrl/⌘-K, so it is a second way out that costs the
              register nothing. */}
          <div className="hidden md:flex flex-1 justify-center px-2">
            <div className="w-full" style={{ maxWidth: 560 }}>
              <GlobalSearch />
            </div>
          </div>
          {/* Spacer pushes actions right on small screens, where search is hidden */}
          <Box sx={{ flexGrow: 1, display: { xs: "block", md: "none" } }} />

          <ModernHeaderActions />
        </div>

        {/* ---- Tier 2: navigation bar (dark) ---- */}
        {showNavTier && (
          <div
            className="flex items-center w-full px-[12px]"
            style={{ height: "var(--navbar-height)", backgroundColor: "var(--primary-color)" }}
          >
            <NavShortcuts />
            <Box sx={{ flexGrow: 1, overflow: "hidden" }}>{menu}</Box>
          </div>
        )}
      </AppBar>

      {/* Navigation drawer reuses the classic sidebar — below `lg`, and on POS at
          any width, where Tier 2 is not rendered. NavMenu is already filtered by
          permissions, so this is the complete set of places this user may go. */}
      {showNavDrawer && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            "& .MuiDrawer-paper": {
              width: 260,
              backgroundColor: "#e7e5e8",
              border: "none",
              // Column so the header pins to the top and only the list scrolls.
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {/* Drawer header.
              This used to be 70px of blank padding on the list, copied from the
              classic shell — where the drawer is PERSISTENT and sits beside a fixed
              AppBar it genuinely has to clear. This one is a TEMPORARY drawer that
              overlays the app bar, so nothing was behind that gap: it just pushed the
              first menu item a third of the way down an empty panel.
              Replaced with a real header, which also gives the close button a home. */}
          <div
            className="flex items-center justify-between gap-2 px-[14px] shrink-0 border-b border-black/10"
            style={{
              height: "var(--utilitybar-height)",
              backgroundColor: "var(--white-color)",
            }}
          >
            <span className="text-[13px] font-semibold uppercase tracking-wider text-gray-500">
              {t("nav:menu", "Menu")}
            </span>
            <Tooltip title={t("common:close", "Close")}>
              <IconButton
                size="small"
                onClick={() => setDrawerOpen(false)}
                aria-label={t("common:close", "Close")}
                sx={{ color: "var(--gray-color)" }}
              >
                <CloseRounded className="!text-[20px]" />
              </IconButton>
            </Tooltip>
          </div>

          {/* Menu starts immediately under the header. flexGrow + auto overflow so a
              long menu scrolls inside the drawer rather than clipping. */}
          <List sx={{ pt: "6px", overflowY: "auto", flexGrow: 1 }}>
            <NavMenu />
          </List>
        </Drawer>
      )}

      {/* Body */}
      <Box
        component="main"
        sx={{
          p: "var(--page-pad)",
          width: "100%",
          mt: bodyMt,
          height: `calc(100vh - ${bodyMt})`,
          overflow: "auto",
          flexGrow: 1,
        }}
      >
        {props.children}
      </Box>
    </div>
  );
}
